import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router';
import axios from 'axios';
import { Form, Field } from 'react-final-form';
import { useToggle } from 'react-use';
import { isEmpty } from 'lodash';
import {
  Container,
  Column,
  Row,
  Card,
  Text,
  Button,
  Flex,
  Box,
} from '../../design-system';
import { FiRefreshCw } from 'react-icons/fi';
import { AutosaveEffect } from '../../hooks/useAutosave';
import useSaveAction from '../../hooks/useSaveAction';
import dayjs from '../../helpers/dates';
import Alert from '../../helpers/alert';
import {
  useData,
  successRefetch,
  isAccessDenied,
} from '../../helpers/data-loader';
import {
  errorMsg,
  currencyFormatter,
  checkPermissions,
  catchPlanDetailError,
  formatDate,
} from '../../helpers/utils';
import Actions from '../../components/Actions';
import Attachments from '../../components/Attachments';
import FieldColumn from '../../components/FieldColumn';
import GeneralInformationFields from './GeneralInformationFields';
import Loadable from '../../components/Loadable';
import ModalDelete from '../../components/ModalDelete';
import NotAccessible from '../../components/NotAccessible';
import ProjectNotes from './ProjectNotes';
import ProjectTitle from './ProjectTitle';
import ReadOnlyColumn from '../../components/ReadOnlyColumn';
import { Table, TableBody } from '../../components/Table';
import { PROJECT_FIELDS, ASSOCIATED_PROJECTS_COLUMNS } from './constants';
import { ERR_MSG, PERMISSIONS } from '../../globalConstants';
import { Session } from '../../contexts/sessionContext';
import CardHeader from '../../components/CardHeader';
import ContactList from '../../components/ContactList';
import DateField from '../../components/DateField';
import RenewalModal from './RenewalModal';
const ProjectEdit = () => {
  const {
    state: { user },
  } = useContext(Session);
  const navigate = useNavigate();
  const { id } = useParams();
  const canCreate = checkPermissions(user.permissions, [
    PERMISSIONS.CREATE_PROJECTS,
  ]);
  const canEdit = checkPermissions(user.permissions, [
    PERMISSIONS.UPDATE_PROJECTS,
  ]);
  const [isModalOpen, toggleModal] = useToggle(false);
  const [lastSave, setLastSave] = useState<any>(null);
  const [isAmountAwardedDisabled, setAmountAwardedDisabled] = useState(true);
  const [isEditing, setEditingStatus] = useState(false);
  const [isOpenDeleteProject, setIsOpenDeleteProject] = useToggle(false);
  const [project, fetchProjectData, projectLoading, error] = useData(
    `/project/${id}`,
    {
      errorMsg: errorMsg('the project'),
    },
  );
  const modelId = id;
  const modelType = 'AppModelsProject';

  const handleChangeInStatus = (status: string) => {
    if (status && status === 'Awarded') {
      setAmountAwardedDisabled(false);
    } else {
      setAmountAwardedDisabled(true);
    }
  };

  const handleDeleteNote = async (note_id: any) => {
    try {
      await axios.delete(`/note/${note_id}`);
      successRefetch(fetchProjectData);
    } catch (delete_error) {
      Alert.error(ERR_MSG);
    }
  };

  const handleUpdateNote = async (values: any) => {
    try {
      await axios.put(`/note/${values.id}`, values);
      successRefetch(fetchProjectData);
    } catch (update_error) {
      Alert.error(ERR_MSG);
    }
  };

  useEffect(() => {
    fetchProjectData();
  }, [id]);

  useEffect(() => {
    if (project) {
      handleChangeInStatus(project.status);
    }
  }, [project]);

  const toggleEditing = () => {
    setEditingStatus(!isEditing);
  };

  const [onSubmit, saving] = useSaveAction(async (values: any) => {
    // Sanitize date fields to prevent backend parsing errors
    const dateFields = [
      'deadline',
      'date_submitted',
      'expected_response',
      'response_date',
      'award_start',
      'award_end',
    ];

    dateFields.forEach((field) => {
      if (
        values[field] === 'Invalid date' ||
        values[field] === '' ||
        values[field] === undefined
      ) {
        values[field] = null;
      }
    });

    // Legacy cleanup for any other invalid date values
    for (const key in values) {
      if (values[key] === 'Invalid date') {
        values[key] = null;
      }
    }
    if (lastSave && values.status === 'Awarded') {
      if (values.award_start === 'Invalid date' || !values.award_start) {
        Alert.error('Warning: No Award Start Date');
        values.award_start = null;
      }
      if (values.award_end === 'Invalid date' || !values.award_end) {
        Alert.error('Warning: No Award End Date');
        values.award_end = null;
      }
    }
    await axios.put('/project/update', {
      ...values,
      funded_program: values.funded_program ? values.funded_program : null,
      amount_awarded:
        values.status === 'Awarded' && values.amount_awarded
          ? values.amount_awarded.replace(/[^0-9.]/g, '')
          : null,
      amount_requested:
        values.amount_requested &&
        values.amount_requested.replace(/[^0-9.]/g, ''),
    });
    setLastSave(dayjs());
  });

  const handleRenewal = async (values: any) => {
    const payload = { ...values, id };
    try {
      const { data } = await axios.post('/project/renew', payload);
      navigate(`/projects/edit/${data.id}`);
      toggleModal();
      Alert.success('Project successfully renewed');
    } catch (err) {
      catchPlanDetailError(err?.response);
    }
  };

  const handleDeleteProject = async () => {
    try {
      await axios.delete(`/project/${project.id}`);
      setIsOpenDeleteProject();
      navigate('/projects', { replace: true });
    } catch {
      Alert.error(ERR_MSG);
    }
  };

  const submissionFields = [
    {
      name: 'submission_link',
      label: 'Submission Link',
    },
    {
      name: 'submission_username',
      label: 'Submission Username',
    },
    {
      name: 'submission_password',
      label: 'Submission Password',
    },
    {
      name: 'submission_notes',
      label: 'Submission Notes',
      multiline: true,
    },
  ];

  if (isAccessDenied(error)) {
    return (
      <NotAccessible
        text="You don't have access to this project."
        redirect="/projects"
      />
    );
  }

  if (error && error.code === 405) {
    return <NotAccessible text="You are not permitted to view this page." />;
  }

  const noProject = isEmpty(project);
  const isRenewed = project?.renewed_at;
  const canRenew = project?.deadline;
  return (
    <Loadable loading={projectLoading || noProject}>
      <Container fluid mt={10} mb={30}>
        <Flex justifyContent="space-between" alignItems="center" pb={2}>
          <Flex flexDirection="column">
            <Text id="project-page-title" pb={2} fontSize={20} fontWeight={600}>
              <ProjectTitle project={project} />
            </Text>
            <Text id="project-page-client" fontSize={16} mb={2}>
              <strong>Client:</strong> {project.client?.fullName}
            </Text>
            <Text id="project-page-funder" fontSize={16}>
              <strong>Funder:</strong> {project.funder?.name}
            </Text>
          </Flex>
          {canCreate && (
            <>
              <Flex flexDirection="column">
                <Button
                  id="renew-project-button"
                  title="Renew Project"
                  aria-label="Renew Project"
                  variant={isRenewed || !canRenew ? 'grey' : 'green'}
                  onClick={toggleModal}
                  disabled={!canRenew}
                >
                  <FiRefreshCw />
                  <Text fontSize={14} ml={2}>
                    Renew Project
                  </Text>
                </Button>

                <Text fontSize={16} color={'red'} mt={10} ml={2}>
                  {canRenew ? `` : 'Please set a project deadline to renew.'}
                </Text>

                <FiRefreshCw />
                <Text fontSize={16} color={'gray'} mt={10} ml={2}>
                  {isRenewed
                    ? `Last renewed on ${formatDate(project.renewed_at)}`
                    : ''}
                </Text>
              </Flex>
              <RenewalModal
                open={isModalOpen}
                toggleModal={toggleModal}
                handleRenewal={handleRenewal}
                projectId={id}
              />
            </>
          )}
        </Flex>
        <Form
          onSubmit={onSubmit}
          initialValues={{
            ...project,
            amount_awarded:
              project.amount_awarded &&
              currencyFormatter.format(project.amount_awarded),
            amount_requested:
              project.amount_requested &&
              currencyFormatter.format(project.amount_requested),
          }}
          render={({ handleSubmit, values }) => (
            <form onSubmit={handleSubmit}>
              <Card mt={10}>
                <>
                  <Flex>
                    <Box width="70%">
                      <CardHeader
                        title="General Info"
                        updatedAt={lastSave || project.updated_at}
                        saving={saving}
                        isSmall
                      />
                      <Card.Body pt={2}>
                        <Row>
                          <GeneralInformationFields
                            project={project}
                            canEdit={canEdit}
                            isEditing={isEditing}
                            user={user}
                            handleChangeInStatus={handleChangeInStatus}
                          />
                        </Row>
                      </Card.Body>
                    </Box>
                    <Box width="30%">
                      <Card.Header>
                        <Text fontSize={18}>Submission Info</Text>
                      </Card.Header>
                      <Card.Body pt={2}>
                        <Row style={{ overflowWrap: 'break-word' }}>
                          {submissionFields.map((field) => {
                            if (canEdit) {
                              if (!isEditing) {
                                return (
                                  <ReadOnlyColumn
                                    key={field.name}
                                    data={project}
                                    field={field}
                                  />
                                );
                              }
                              return (
                                <FieldColumn
                                  key={field.name}
                                  width={300}
                                  {...field}
                                />
                              );
                            }
                            return (
                              <ReadOnlyColumn
                                key={field.name}
                                data={project}
                                field={field}
                              />
                            );
                          })}
                        </Row>
                      </Card.Body>
                    </Box>
                  </Flex>
                  <Card.Header>
                    <Text fontSize={18}>Award Info</Text>
                  </Card.Header>
                  <Card.Body pt={2}>
                    <Row>
                      {PROJECT_FIELDS.awardInfo.map((field) => {
                        if (canEdit) {
                          if (!isEditing) {
                            return (
                              <ReadOnlyColumn
                                key={field.name}
                                data={project}
                                field={field}
                              />
                            );
                          }

                          if (field.type === 'date') {
                            return (
                              <Column key={field.name} pb={2} width={300}>
                                <Field name={field.name}>
                                  {({ input }) => {
                                    return (
                                      <DateField
                                        name={field.name}
                                        label={field.label}
                                        selected={input.value}
                                        onChange={(value) =>
                                          input.onChange(value)
                                        }
                                        showPastDue={false}
                                      />
                                    );
                                  }}
                                </Field>
                              </Column>
                            );
                          }

                          let newField;
                          if (field.name === 'amount_awarded') {
                            newField = {
                              ...field,
                              disabled: isAmountAwardedDisabled,
                            };
                            if (isAmountAwardedDisabled) {
                              newField.placeholder =
                                'Project status must be Awarded';
                              newField.value = '';
                            }
                          }
                          if (field.name === 'empty') {
                            return (
                              <Column key={field.name} pb={3} width={300} />
                            );
                          }
                          return (
                            <FieldColumn
                              key={field.name}
                              width={300}
                              {...(newField || field)}
                            />
                          );
                        }
                        return (
                          <ReadOnlyColumn
                            key={field.name}
                            data={project}
                            field={field}
                          />
                        );
                      })}
                    </Row>
                    {canEdit &&
                      (isEditing ? (
                        <>
                          <AutosaveEffect
                            data={values}
                            onSave={handleSubmit}
                            interval={600}
                          />
                          <Flex m={10} justifyContent="flex-end">
                            <Button
                              id="exit-edit-button"
                              title="Finish"
                              aria-label="Finish Editing Project"
                              onClick={async () => {
                                await handleSubmit(values);
                                successRefetch(fetchProjectData);
                                toggleEditing();
                              }}
                              loading={saving}
                            >
                              Finish Editing
                            </Button>
                          </Flex>
                        </>
                      ) : (
                        <Flex m={10} justifyContent="flex-end">
                          <Button
                            id="enter-edit-button"
                            title="Edit"
                            aria-label="Edit Project"
                            type="button"
                            onClick={toggleEditing}
                          >
                            Edit
                          </Button>
                          {!user.isClient && (
                            <Button
                              ml={20}
                              id="delete-button"
                              title="Delete"
                              aria-label="Delete Project"
                              type="button"
                              variant="primaryText"
                              onClick={setIsOpenDeleteProject}
                            >
                              Delete
                            </Button>
                          )}
                        </Flex>
                      ))}
                  </Card.Body>
                </>
              </Card>
            </form>
          )}
        />

        <ContactList
          modelType="project"
          modelID={project.id}
          assignedContacts={project.assignedContacts || []}
          allContacts={project.allContacts || []}
          refetch={fetchProjectData}
          canEdit={canEdit}
        />
        <Actions
          data={project.actions || []}
          modelId={modelId}
          modelType={modelType}
          refetch={fetchProjectData}
          maxHeight="25rem"
          canEdit={canEdit}
          handleDeleteNote={handleDeleteNote}
          handleUpdateNote={handleUpdateNote}
        />
        <ProjectNotes
          data={project.notes || []}
          projectId={id}
          canEdit={canEdit}
          refetch={fetchProjectData}
          handleDeleteNote={handleDeleteNote}
        />
        <Attachments
          canEdit={canEdit}
          data={project.files || []}
          modelId={modelId}
          modelType={modelType}
          clientId={project.client_id}
          refetch={fetchProjectData}
          maxHeight="25rem"
          disabled={!canEdit}
        />
        <Card mt={10}>
          <Card.Header>
            <Text>Associated Projects</Text>
          </Card.Header>
          <TableBody {...({ maxHeight: '25rem' } as any)}>
            <Table
              columns={ASSOCIATED_PROJECTS_COLUMNS}
              data={
                project?.associatedProjects?.map((item: any) => ({
                  ...item,
                  canEdit,
                })) || []
              }
            />
          </TableBody>
        </Card>
        <ModalDelete
          entity="project"
          open={isOpenDeleteProject}
          onCancel={setIsOpenDeleteProject}
          onDelete={handleDeleteProject}
        />
      </Container>
    </Loadable>
  );
};

export default ProjectEdit;
